import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router } from '@angular/router';

import { clientToken } from '../constants/auth-state';
import {
  PRODUCT_ACCESS_DENIED_URL,
  ROUTE_PRODUCT_DATA_KEY,
} from '../constants/product.constants';
import type { ProductCode } from '../models/product.model';
import { ProductAccessService } from '../services/product-access.service';

/**
 * Guard on every page inside the main layout. Blocks pages of products the user is not
 * assigned to, makes a multi-product user choose a product first, and keeps the user in the
 * product chosen for this session (switching products means logging in again).
 */
export const productAccessGuard: CanActivateChildFn = async (childRoute) => {
  // Prospect pages are protected by their own link token, not by product assignment.
  if (clientToken()) {
    return true;
  }

  const router = inject(Router);
  const productAccessService = inject(ProductAccessService);

  await productAccessService.load();

  const routeProduct = findRouteProduct(childRoute);
  if (routeProduct && !productAccessService.hasAccess(routeProduct)) {
    return router.parseUrl(PRODUCT_ACCESS_DENIED_URL);
  }

  const entryUrl = await productAccessService.resolveEntryUrl();
  const selectedProduct = productAccessService.selectedProduct();

  if (!selectedProduct) {
    return router.parseUrl(entryUrl);
  }

  if (routeProduct && routeProduct !== selectedProduct) {
    return router.parseUrl(entryUrl);
  }

  return true;
};

/** Returns the product tag of the route or its nearest tagged ancestor, or `null` for shared pages. */
function findRouteProduct(route: ActivatedRouteSnapshot): ProductCode | null {
  for (const snapshot of [...route.pathFromRoot].reverse()) {
    const product = snapshot.routeConfig?.data?.[ROUTE_PRODUCT_DATA_KEY] as ProductCode | undefined;
    if (product) {
      return product;
    }
  }

  return null;
}
